import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import { Datum } from 'src/app/models/common/datum'
import { PlanType } from 'src/app/models/service-plan/plan-type'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

// Step 1 — Client & plan type (story e8-1 AC10). The only real step this story ships;
// every other step the engine can reach renders StepPlaceholderComponent instead.
@Component({
  selector: 'app-service-plan-plan-type-step',
  templateUrl: './plan-type-step.component.html',
  styleUrls: ['./plan-type-step.component.scss'],
  standalone: false,
})
export class PlanTypeStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  form!: FormGroup<{
    planType: FormControl<PlanType | null>
    hasNdis: FormControl<boolean | null>
  }>

  planTypeOptions: Datum[] = []
  planTypeLabel = ''
  planTypeControlId = ''
  hasNdisLabel = ''
  hasNdisControlId = ''

  private subscription?: Subscription

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.planTypeLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.PLAN_TYPE.FIELD_LABEL',
    )
    this.planTypeControlId = LabelToId(this.planTypeLabel)
    this.hasNdisLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.PLAN_TYPE.HAS_NDIS_LABEL',
    )
    this.hasNdisControlId = LabelToId(this.hasNdisLabel)
    this.planTypeOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_TRANSITION',
        ),
        value: 'transition',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_TRANSITION_DESC',
        ),
      },
      {
        label: this.translate.instant('SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_GSP'),
        value: 'gsp',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_GSP_DESC',
        ),
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_FIRST',
        ),
        value: 'first',
        description: this.translate.instant(
          'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_FIRST_DESC',
        ),
      },
    ]

    const payload = this.state.getPayload()
    this.form = new FormGroup({
      planType: new FormControl<PlanType | null>(payload.planType, {
        validators: Validators.required,
      }),
      hasNdis: new FormControl<boolean | null>(payload.ndis.hasNdis),
    })

    this.applyHasNdisValidator(payload.planType)

    this.subscription = this.form.valueChanges.subscribe((value) => {
      this.applyHasNdisValidator(value.planType ?? null)
      this.state.updatePayload({
        planType: value.planType ?? null,
        ndis: {
          ...this.state.getPayload().ndis,
          // Read the control's own (possibly just-cleared) value rather than the
          // emitted snapshot's `value.hasNdis` — applyHasNdisValidator may have
          // just reset it to null above, and the stale snapshot would otherwise
          // carry a now-inapplicable answer back into the payload.
          hasNdis: this.form.controls.hasNdis.value ?? null,
        },
      })
    })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  get showHasNdis(): boolean {
    return this.form?.controls.planType.value === 'first'
  }

  // ServicePlanStepComponent contract — the shell's Continue button reads this directly
  // (non-mutating), unlike isValid() below which calls markAllAsTouched() on click.
  get isLiveValid(): boolean {
    return this.form?.valid ?? false
  }

  // ServicePlanStepComponent contract — consumed by the stepper shell's Continue button.
  isValid(): boolean {
    this.form.markAllAsTouched()
    return this.form.valid
  }

  getValidationErrors(): ServicePlanFieldError[] {
    const errors: ServicePlanFieldError[] = []
    if (this.form.controls.planType.invalid) {
      errors.push({
        controlId: this.planTypeControlId,
        messageKey: 'SERVICE_PLAN.VALIDATION.PLAN_TYPE_REQUIRED',
      })
    }
    if (this.form.controls.hasNdis.invalid) {
      errors.push({
        controlId: this.hasNdisControlId,
        messageKey: 'SERVICE_PLAN.VALIDATION.HAS_NDIS_REQUIRED',
      })
    }
    return errors
  }

  private applyHasNdisValidator(planType: PlanType | null): void {
    const control = this.form.controls.hasNdis
    if (planType === 'first') {
      control.setValidators(Validators.required)
    } else {
      control.clearValidators()
      // The Has-NDIS question only applies to 'first' — once it's hidden, its
      // prior answer (if any) is no longer applicable and must not be carried
      // forward silently into the payload.
      if (control.value !== null) {
        control.setValue(null, { emitEvent: false })
      }
    }
    control.updateValueAndValidity({ emitEvent: false })
  }
}
